import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ErrorMessage } from 'components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Activity } from 'services';
import { ModelsActions } from "store/actions";
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import { DateTimePicker } from '@material-ui/pickers';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  datepicker: {
    marginTop: 0
  }
}));

const schema = {
  title: {
    presence: { allowEmpty: false, message: "is required" }
  },
  status: {
    presence: { allowEmpty: false, message: "is required" }
  },
  content: {
    presence: { allowEmpty: false, message: "is required" },
  },
  timestamp: {
    presence: { allowEmpty: false, message: "is required" },
  },
};

const messages = {

}

let mounted = false;

const ActivityEdit = props => {
  const { open, onClose, className, ...rest } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.activity);
  const dispatch = useDispatch();
  const [formState, setFormState] = useState({
    values: { ...model },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    mounted = true;
    return () => { mounted = false };
  }, [])

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  if (!open) {
    return null;
  }

  const handleClose = event => {
    event.preventDefault();

    if (Object.keys(formState.errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      const response = await Activity.update(model.id, { ...formState.values });
      dispatch(ModelsActions.update_model({ activity: response.model }));
    }).finally(() => {
      setLoading(false);
      onClose();
    });

  }

  const handleFieldChange = event => {
    event.persist();
    mounted && setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };


  const handleDateChange = (date, field) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [field]: date
      },
      touched: {
        ...formState.touched,
        [field]: true
      }
    }));
  };

  return (
    <Modal
      onClose={onClose}
      open={open}>
      <Card
        {...rest}
        className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Edit Activity
            </Typography>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <CustTextField name="title" label="Title" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <DateTimePicker
                  className={classes.datepicker}
                  fullWidth
                  variant="inline"
                  format="YYYY/MM/DD - HH:mm"
                  autoOk
                  id="timestamp"
                  label="Timestamp"
                  name="timestamp"
                  value={formState.values.timestamp}
                  required
                  onChange={(date) =>
                    handleDateChange(date, "timestamp")
                  }
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="status" dropdown label="Status" formState={formState} handleFieldChange={handleFieldChange} options={[{ label: "Draft", value: "draft" }, { label: "Published", value: "published" }, { label: "Archived", value: "archived" }]} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField multiline name="content" label="Content" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <ErrorMessage message={errorMessage} map={messages} className={classes.error} />
              <Grid item />
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleClose} variant="contained" disabled={loading} >Save</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
};

ActivityEdit.propTypes = {
  className: PropTypes.string,
  model: PropTypes.any,
  setModel: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

ActivityEdit.defaultProps = {
  open: false,
  onClose: () => { }
};


const CustTextField = props => {
  let { label, name, formState, handleFieldChange, type, dropdown, options, required, multiline } = props;
  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <TextField
      fullWidth
      multiline={multiline}
      label={label}
      helperText={hasError(name) ? formState.errors[name][0] : null}
      name={name}
      onChange={handleFieldChange}
      value={formState.values[name]}
      error={hasError(name)}
      variant="outlined"
      type={type}
      select={dropdown}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: dropdown }}
      required={required}
    >
      {options && options.length > 0 && options.map((opt, index) => (
        <option key={index} value={opt.value}>{opt.label}</option>
      ))
      }
    </TextField>
  );
}


export default ActivityEdit;
